// Dev-mode recorder diagnostics. Added to chase camera-only recordings whose video stops while
// audio carries on (and, on the same Linux machines, the renderer dying with SIGILL): the file
// alone can't say whether the camera stopped delivering frames, the compositor stopped drawing,
// or the chunks stopped reaching IndexedDB / the upload queue. Once a second while recording,
// this samples every stage of that pipeline and logs one line to the console.
//
// Samples are also written to localStorage: a renderer crash takes the console (and DevTools)
// with it, so the previous session's log has to survive a reload to be read at all. Collection
// only runs when dev mode is on at the start of a recording; with it off, every call here is a
// no-op apart from bookkeeping on objects the recorder already holds.
import { isDevMode } from "../lib/devMode";

const STORAGE_KEY = "grackle.recDiag";
const SAMPLE_MS = 1000;
const MAX_SAMPLES = 600; // ten minutes
const MAX_EVENTS = 200;
const LONG_TASK_MS = 200;

type TrackName = "camera" | "canvas" | "mic" | "screen";

/** Chrome's `MediaStreamTrack.stats` (MediaStreamTrackVideoStats); not in the DOM lib yet. */
interface VideoTrackStats {
  totalFrames?: number;
  deliveredFrames?: number;
  discardedFrames?: number;
}

export interface DiagSample {
  /** Seconds since recording started. */
  t: number;
  /** Frames per second each video track produced in this interval, from `track.stats`; null
   * where the browser doesn't expose it. */
  cameraFps: number | null;
  canvasFps: number | null;
  /** How far the hidden camera <video> advanced this interval (s); ~1 while it plays. */
  cameraVideoAdvance: number | null;
  cameraVideoSize: string | null;
  tracks: Partial<Record<TrackName, string>>;
  draws: number;
  drawsSkipped: number;
  chunks: number;
  chunkBytes: number;
  saved: number;
  saveFailed: number;
  queued: number;
  /** Chunks captured so far that haven't reached the upload queue yet. */
  pending: number;
  longTasks: number;
  longestTaskMs: number;
  /** How late this sample's timer fired (ms) — a blocked main thread shows up here too. */
  tickLagMs: number;
  recorder: string;
  hidden: boolean;
}

export interface DiagSession {
  startedAt: string;
  samples: DiagSample[];
  events: string[];
}

type Counter = "draws" | "drawsSkipped" | "chunks" | "saved" | "saveFailed" | "queued";

function trackState(track: MediaStreamTrack): string {
  return track.readyState === "live" ? (track.muted ? "muted" : "live") : track.readyState;
}

function totalFrames(track: MediaStreamTrack | undefined): number | null {
  const stats = (track as (MediaStreamTrack & { stats?: VideoTrackStats }) | undefined)?.stats;
  return typeof stats?.totalFrames === "number" ? stats.totalFrames : null;
}

export function formatSample(s: DiagSample): string {
  const fps = (v: number | null) => (v === null ? "?" : String(v));
  const tracks = Object.entries(s.tracks)
    .map(([name, state]) => `${name}:${state}`)
    .join(" ");
  return [
    `t=${s.t}s`,
    `cam=${fps(s.cameraFps)}fps`,
    `camVideo=${s.cameraVideoSize ?? "-"} +${s.cameraVideoAdvance === null ? "?" : s.cameraVideoAdvance.toFixed(2)}s`,
    `draws=${s.draws}${s.drawsSkipped ? ` skipped=${s.drawsSkipped}` : ""}`,
    `canvas=${fps(s.canvasFps)}fps`,
    `chunks=${s.chunks} ${Math.round(s.chunkBytes / 1024)}KB`,
    `saved=${s.saved}${s.saveFailed ? ` saveFailed=${s.saveFailed}` : ""}`,
    `queued=${s.queued} pending=${s.pending}`,
    `long=${s.longTasks}${s.longTasks ? `(max ${Math.round(s.longestTaskMs)}ms)` : ""}`,
    `lag=${Math.round(s.tickLagMs)}ms`,
    `rec=${s.recorder}`,
    s.hidden ? "HIDDEN" : "",
    tracks,
  ]
    .filter(Boolean)
    .join(" ");
}

function readStored(): DiagSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as DiagSession) : null;
  } catch {
    return null;
  }
}

class RecorderDiagnostics {
  private active = false;
  private session: DiagSession | null = null;
  /** What localStorage held before this page's first recording — typically the run that
   * crashed. Read once, before anything here overwrites it. */
  private readonly previous: DiagSession | null = readStored();

  private tracks = new Map<TrackName, MediaStreamTrack>();
  private trackCleanups: Array<() => void> = [];
  private cameraVideo: HTMLVideoElement | null = null;
  private recorder: MediaRecorder | null = null;

  private counters: Record<Counter, number> = { draws: 0, drawsSkipped: 0, chunks: 0, saved: 0, saveFailed: 0, queued: 0 };
  private chunkBytes = 0;
  private totalChunks = 0;
  private totalQueued = 0;
  private longTasks = 0;
  private longestTaskMs = 0;

  private lastFrames: Partial<Record<TrackName, number | null>> = {};
  private lastVideoTime: number | null = null;
  private startedAtMs = 0;
  private nextTickAt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private teardown: Array<() => void> = [];
  private listeners = new Set<() => void>();

  get enabled(): boolean {
    return this.active;
  }

  /** Register a track as soon as it exists (before recording starts is fine). Its ended /
   * mute / unmute transitions are logged as events while a session is running. */
  attachTrack(name: TrackName, track: MediaStreamTrack | undefined | null): void {
    if (!track) return;
    this.tracks.set(name, track);
    const onEnded = () => this.event(`${name} track ended`);
    const onMute = () => this.event(`${name} track muted`);
    const onUnmute = () => this.event(`${name} track unmuted`);
    track.addEventListener("ended", onEnded);
    track.addEventListener("mute", onMute);
    track.addEventListener("unmute", onUnmute);
    this.trackCleanups.push(() => {
      track.removeEventListener("ended", onEnded);
      track.removeEventListener("mute", onMute);
      track.removeEventListener("unmute", onUnmute);
    });
  }

  attachCameraVideo(video: HTMLVideoElement): void {
    this.cameraVideo = video;
  }

  /** Called from the capture teardown: forget the old tracks so a re-request doesn't sample
   * (or log events from) streams that were stopped on purpose. */
  detachAll(): void {
    for (const cleanup of this.trackCleanups) cleanup();
    this.trackCleanups = [];
    this.tracks.clear();
    this.cameraVideo = null;
  }

  count(counter: Counter, n = 1): void {
    if (!this.active) return;
    this.counters[counter] += n;
    if (counter === "queued") this.totalQueued += n;
  }

  chunk(bytes: number): void {
    if (!this.active) return;
    this.counters.chunks += 1;
    this.chunkBytes += bytes;
    this.totalChunks += 1;
  }

  /** The compositor's per-frame hook: `drawn` is false when it had nothing to draw (camera
   * frame size 0), which on its own stops the canvas producing frames. */
  readonly draw = (drawn: boolean): void => {
    if (!this.active) return;
    this.counters[drawn ? "draws" : "drawsSkipped"] += 1;
  };

  event(message: string): void {
    if (!this.active || !this.session) return;
    const line = `t=${this.elapsedSeconds()}s ${message}`;
    this.session.events.push(line);
    if (this.session.events.length > MAX_EVENTS) this.session.events.shift();
    console.warn("[rec-diag]", line);
    this.persist();
    this.emit();
  }

  start(recorder: MediaRecorder | null): void {
    if (this.active || !isDevMode()) return;
    this.active = true;
    this.recorder = recorder;
    this.session = { startedAt: new Date().toISOString(), samples: [], events: [] };
    this.resetCounters();
    this.totalChunks = 0;
    this.totalQueued = 0;
    this.startedAtMs = performance.now();
    this.lastFrames = {};
    for (const [name, track] of this.tracks) this.lastFrames[name] = totalFrames(track);
    this.lastVideoTime = this.cameraVideo?.currentTime ?? null;
    this.listenPage();
    if (recorder) {
      const onError = () => this.event("MediaRecorder error");
      recorder.addEventListener("error", onError);
      this.teardown.push(() => recorder.removeEventListener("error", onError));
    }
    console.info("[rec-diag] started — one line per second; copy it from the recorder's dev panel");
    this.nextTickAt = performance.now() + SAMPLE_MS;
    this.timer = setTimeout(this.tick, SAMPLE_MS);
    this.persist();
    this.emit();
  }

  stop(): void {
    if (!this.active) return;
    this.event("recording stopped");
    this.active = false;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    for (const t of this.teardown) t();
    this.teardown = [];
    this.recorder = null;
    this.emit();
  }

  current(): DiagSession | null {
    return this.session;
  }

  previousSession(): DiagSession | null {
    return this.previous;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private readonly tick = (): void => {
    if (!this.active || !this.session) return;
    const now = performance.now();
    const sample = this.sample(now - this.nextTickAt);
    this.session.samples.push(sample);
    if (this.session.samples.length > MAX_SAMPLES) this.session.samples.shift();
    console.info("[rec-diag]", formatSample(sample));
    this.persist();
    this.emit();
    this.nextTickAt = now + SAMPLE_MS;
    this.timer = setTimeout(this.tick, SAMPLE_MS);
  };

  private sample(tickLagMs: number): DiagSample {
    const fps = (name: TrackName): number | null => {
      const frames = totalFrames(this.tracks.get(name));
      const last = this.lastFrames[name];
      this.lastFrames[name] = frames;
      return frames === null || last === null || last === undefined ? null : frames - last;
    };
    const video = this.cameraVideo;
    const videoTime = video ? video.currentTime : null;
    const advance = videoTime === null || this.lastVideoTime === null ? null : videoTime - this.lastVideoTime;
    this.lastVideoTime = videoTime;

    const tracks: Partial<Record<TrackName, string>> = {};
    for (const [name, track] of this.tracks) tracks[name] = trackState(track);

    const sample: DiagSample = {
      t: this.elapsedSeconds(),
      cameraFps: fps("camera"),
      canvasFps: fps("canvas"),
      cameraVideoAdvance: advance,
      cameraVideoSize: video ? `${video.videoWidth}x${video.videoHeight}${video.paused ? " paused" : ""}` : null,
      tracks,
      draws: this.counters.draws,
      drawsSkipped: this.counters.drawsSkipped,
      chunks: this.counters.chunks,
      chunkBytes: this.chunkBytes,
      saved: this.counters.saved,
      saveFailed: this.counters.saveFailed,
      queued: this.counters.queued,
      pending: this.totalChunks - this.totalQueued,
      longTasks: this.longTasks,
      longestTaskMs: this.longestTaskMs,
      tickLagMs: Math.max(0, tickLagMs),
      recorder: this.recorder?.state ?? "-",
      hidden: typeof document !== "undefined" && document.visibilityState === "hidden",
    };
    this.resetCounters();
    return sample;
  }

  private resetCounters(): void {
    this.counters = { draws: 0, drawsSkipped: 0, chunks: 0, saved: 0, saveFailed: 0, queued: 0 };
    this.chunkBytes = 0;
    this.longTasks = 0;
    this.longestTaskMs = 0;
  }

  /** Tab visibility and Page Lifecycle transitions (a hidden tab stops requestAnimationFrame,
   * which stops the compositor), plus main-thread long tasks where the browser reports them. */
  private listenPage(): void {
    const onVisibility = () => this.event(`page ${document.visibilityState}`);
    const onFreeze = () => this.event("page frozen");
    const onResume = () => this.event("page resumed");
    document.addEventListener("visibilitychange", onVisibility);
    document.addEventListener("freeze", onFreeze);
    document.addEventListener("resume", onResume);
    this.teardown.push(() => {
      document.removeEventListener("visibilitychange", onVisibility);
      document.removeEventListener("freeze", onFreeze);
      document.removeEventListener("resume", onResume);
    });
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.duration < LONG_TASK_MS) continue;
          this.longTasks += 1;
          this.longestTaskMs = Math.max(this.longestTaskMs, entry.duration);
        }
      });
      observer.observe({ type: "longtask", buffered: false });
      this.teardown.push(() => observer.disconnect());
    } catch {
      // No Long Tasks API (Firefox, Safari): tickLagMs still shows a blocked main thread.
    }
  }

  private elapsedSeconds(): number {
    return Math.round((performance.now() - this.startedAtMs) / 1000);
  }

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.session));
    } catch {
      // Quota or a locked-down localStorage: the console still has every line.
    }
  }

  private emit(): void {
    this.listeners.forEach((l) => l());
  }
}

export const recDiag = new RecorderDiagnostics();

/** A whole session as pasteable text: header, events, then one line per sample. */
export function sessionText(session: DiagSession): string {
  return [
    `rec-diag session started ${session.startedAt} (${navigator.userAgent})`,
    "events:",
    ...(session.events.length ? session.events.map((e) => `  ${e}`) : ["  (none)"]),
    "samples:",
    ...session.samples.map((s) => `  ${formatSample(s)}`),
  ].join("\n");
}
